// ============================================================================
// COLLAPSIBLE SECTION — Sección colapsable reutilizable
// Persistencia por userId en localStorage
// ============================================================================
import { useState, useEffect, ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface CollapsibleSectionProps {
  /** Identificador único de la sección (usado para persistencia) */
  id: string;
  /** Título del bloque */
  title: string;
  /** Icono lucide-react (ej: <Building2 className="h-5 w-5 text-gray-400" />) */
  icon?: ReactNode;
  /** Badge/contador a la derecha del título */
  badge?: ReactNode;
  /** Acciones a la derecha (botones) — se muestran incluso colapsado */
  actions?: ReactNode;
  /** Estado inicial si no hay valor persistido */
  defaultOpen?: boolean;
  /** userId para namespacing de la persistencia (evita colisiones entre usuarios) */
  userId?: string;
  /** Si true, fuerza el estado inicial y no se persiste */
  disablePersistence?: boolean;
  children: ReactNode;
}

export function CollapsibleSection({
  id,
  title,
  icon,
  badge,
  actions,
  defaultOpen = false,
  userId,
  disablePersistence = false,
  children,
}: CollapsibleSectionProps) {
  const storageKey = userId ? `imi_patient360_${userId}_${id}` : `imi_patient360_${id}`;

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (disablePersistence) return defaultOpen;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === null) return defaultOpen;
      return stored === 'true';
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    if (disablePersistence) return;
    try {
      localStorage.setItem(storageKey, String(isOpen));
    } catch {
      /* localStorage no disponible o lleno: ignorar */
    }
  }, [isOpen, storageKey, disablePersistence]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-3 flex-1 text-left group"
          aria-expanded={isOpen}
          aria-controls={`section-${id}`}
        >
          <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
            {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </span>
          {icon}
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          {badge && <span className="ml-1">{badge}</span>}
        </button>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {isOpen && (
        <div id={`section-${id}`} className="p-4 sm:p-6">
          {children}
        </div>
      )}
    </div>
  );
}

export default CollapsibleSection;
